import { Injectable, Injector } from '@angular/core';
import { BehaviorSubject, of } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { KdsOrderSortDirection } from 'src/app/appEnums/enums';
import { IBasicInfo } from 'src/app/appInterfaces/IBasicInfo';
import { BasicInfoServices } from '../basic-info/basic-info.service';
import { RestaurantInfoResolver } from '../restaurantInfo/restaurant-info.resolver';


@Injectable({
  providedIn: 'root'
})
export class SettingsResolver {
  baseSize: number = 10;

  rootFontSize = new BehaviorSubject<number>(this.baseSize);
  kdsOrderSortDirection = new BehaviorSubject<KdsOrderSortDirection>(KdsOrderSortDirection.NewestFirst);
  htmlRoot:HTMLElement = <HTMLElement> document.getElementsByTagName("html")[0];
  private readonly kdsOrderSortDirectionStorageKey = 'kdsOrderSortDirection';
  private restaurantSettingsSubscribed = false;
  private savingKdsOrderSortDirection = false;
  
  constructor(
    private injector: Injector,
    private _basicInfoService: BasicInfoServices,
  ) {
    this.getRootFontSizeFromLocalStorage();
    this.restoreKdsOrderSortDirection();
  }

  restoreFontSize(){
    this.rootFontSize.next(this.baseSize);
    localStorage.setItem('rootFontSize', JSON.stringify(this.baseSize));
  }


  updateRootFontSize(fontSize:number){
    this.rootFontSize.next(fontSize);
    localStorage.setItem('rootFontSize', JSON.stringify(fontSize));
  }
  

  setRootFontSize(){
    this.rootFontSize.subscribe(rfs =>{
      if (this.htmlRoot != null) {
        this.htmlRoot.style.fontSize = rfs + 'px';
      }
    })
  }


  getRootFontSizeFromLocalStorage(){
    const rootFontSize= JSON.parse(localStorage.getItem('rootFontSize'));
    this.rootFontSize.next(rootFontSize);
  }

  bindRestaurantSettings() {
    if (this.restaurantSettingsSubscribed) {
      return;
    }

    this.restaurantSettingsSubscribed = true;
    this.injector.get(RestaurantInfoResolver).restaurantBasicInfo.subscribe((basicInfo) => {
      if (this.savingKdsOrderSortDirection) {
        return;
      }
      const direction = this.normalizeKdsOrderSortDirection(basicInfo?.kdsOrderSortDirection);
      if (direction) {
        this.setKdsOrderSortDirectionLocal(direction);
      }
    });
  }

  updateKdsOrderSortDirection(direction: KdsOrderSortDirection | string) {
    const nextDirection = this.normalizeKdsOrderSortDirection(direction);
    if (!nextDirection || nextDirection === this.kdsOrderSortDirection.value) {
      return;
    }

    const previousDirection = this.kdsOrderSortDirection.value;
    const previousBasicInfo = this.getBasicInfoFromLocalStorage();
    this.setKdsOrderSortDirectionLocal(nextDirection);

    if (!previousBasicInfo) {
      return;
    }

    const updatedBasicInfo: IBasicInfo = {
      ...previousBasicInfo,
      kdsOrderSortDirection: nextDirection,
    };
    this.savingKdsOrderSortDirection = true;
    this.setBasicInfo(updatedBasicInfo);
    this._basicInfoService.updateAccountInfo(updatedBasicInfo).pipe(
      take(1),
      catchError((error) => {
        console.error('Unable to save KDS order sort direction', error);
        this.setKdsOrderSortDirectionLocal(previousDirection);
        this.setBasicInfo(previousBasicInfo);
        return of(null);
      }),
    ).subscribe((res) => {
      if (!res) {
        return;
      }
      const savedDirection = this.normalizeKdsOrderSortDirection(res?.kdsOrderSortDirection) || nextDirection;
      this.setBasicInfo({
        ...updatedBasicInfo,
        ...res,
        kdsOrderSortDirection: savedDirection,
      });
    }).add(() => {
      this.savingKdsOrderSortDirection = false;
    });
  }

  private restoreKdsOrderSortDirection() {
    const basicInfoDirection = this.normalizeKdsOrderSortDirection(
      this.getBasicInfoFromLocalStorage()?.kdsOrderSortDirection
    );
    const localDirection = this.normalizeKdsOrderSortDirection(
      localStorage.getItem(this.kdsOrderSortDirectionStorageKey)
    );
    this.setKdsOrderSortDirectionLocal(
      basicInfoDirection || localDirection || KdsOrderSortDirection.NewestFirst
    );
  }

  private setKdsOrderSortDirectionLocal(direction: KdsOrderSortDirection) {
    localStorage.setItem(this.kdsOrderSortDirectionStorageKey, direction);
    if (this.kdsOrderSortDirection.value !== direction) {
      this.kdsOrderSortDirection.next(direction);
    }
  }

  private normalizeKdsOrderSortDirection(direction: any): KdsOrderSortDirection | null {
    if (direction === KdsOrderSortDirection.NewestFirst || direction === 'newest') {
      return KdsOrderSortDirection.NewestFirst;
    }
    if (direction === KdsOrderSortDirection.OldestFirst || direction === 'oldest') {
      return KdsOrderSortDirection.OldestFirst;
    }
    return null;
  }

  private getBasicInfoFromLocalStorage(): IBasicInfo | null {
    try {
      return JSON.parse(localStorage.getItem('basic-info') || 'null') as IBasicInfo | null;
    } catch {
      return null;
    }
  }

  private setBasicInfo(basicInfo: IBasicInfo) {
    localStorage.setItem('basic-info', JSON.stringify(basicInfo));
    this.injector.get(RestaurantInfoResolver).setBasicInfo(basicInfo);
  }
}
